import useTerms from "../../../hooks/useTerms";
import React, { useState } from "react";
import { Box, Button, LinearProgress, Typography, alpha } from "@mui/material";
import {
  Inventory2Outlined as PackageIcon,
  RemoveCircleOutline as UseIcon,
  UndoRounded as UndoIcon,
} from "@mui/icons-material";
import { ActionButton, EmptyState } from "../../atoms";
import { PromptDialog } from "../../molecules";
import ConfirmDialog from "../../common/ConfirmDialog";
import { DetailList, StatusPill } from "../ClinicRecordList";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { formatMoney } from "../../../utils/money";
import { FormDrawer, PACKAGE_STATUS, USAGE_SOURCE_LABELS, dateTimeTr, dateTr } from "./billingParts";

/**
 * Satılmış paketin ayrıntısı: kalan hak, kullanım geçmişi (geri alınanlar
 * soluk), elle seans düşme, kullanım geri alma, paketi iptal etme.
 */
export default function PackageDetailDrawer({
  open,
  onClose,
  pkg,
  canManage,
  busy,
  onConsume,
  onRevert,
  onCancel,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const [askUse, setAskUse] = useState(false);
  const [askCancel, setAskCancel] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null);

  if (!pkg) return null;
  const st = PACKAGE_STATUS[pkg.status] || PACKAGE_STATUS.active;
  const usages = [...(pkg.usages || [])].sort((a, b) => new Date(b.at) - new Date(a.at));
  const canUse = canManage && pkg.status === "active" && pkg.remaining > 0;
  const progress = pkg.total ? Math.round((pkg.used / pkg.total) * 100) : 0;

  return (
    <>
      <FormDrawer
        open={open}
        onClose={onClose}
        icon={<PackageIcon />}
        title={pkg.template?.name || "Paket"}
        subtitle={`${pkg.template?.serviceName || "Hizmet"} · ${dateTr(pkg.startedAt)} tarihinde satıldı`}
        width={520}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>
                {pkg.used}/{pkg.total} seans kullanıldı · {pkg.remaining} kaldı
              </Typography>
              <StatusPill label={st.label} tone={st.tone} />
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: COLORS.slate100,
                "& .MuiLinearProgress-bar": { bgcolor: palette.primary, borderRadius: 2 },
              }}
            />
          </Box>

          {canManage && (canUse || pkg.status !== "cancelled") ? (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {canUse ? (
                <ActionButton
                  variant="confirm"
                  palette={palette}
                  icon={<UseIcon />}
                  label="Elle seans düş"
                  loading={busy}
                  onClick={() => setAskUse(true)}
                />
              ) : null}
              {pkg.status !== "cancelled" ? (
                <ActionButton variant="delete" tone="outline" label="Paketi iptal et" onClick={() => setAskCancel(true)} />
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ mx: -2 }}>
            <DetailList
              items={[
                { label: "Paket fiyatı", value: formatMoney(pkg.template?.price) },
                { label: "Seans sayısı", value: `${pkg.total} seans` },
                { label: "Geçerlilik", value: `${dateTr(pkg.startedAt)} – ${dateTr(pkg.expiresAt)}` },
                pkg.note ? { label: "Not", value: pkg.note } : null,
                pkg.status === "cancelled"
                  ? { label: "İptal gerekçesi", value: `${pkg.cancellation?.reason || "—"} · ${dateTr(pkg.cancellation?.at)}` }
                  : null,
              ]}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: TYPO.caption,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: COLORS.slate500,
                mb: 1,
              }}
            >
              Kullanım geçmişi
            </Typography>
            {usages.length === 0 ? (
              <EmptyState
                variant="illustrated-sm"
                icon={<PackageIcon />}
                title="Henüz seans düşülmedi"
                description="Pakete bağlı randevu tamamlandıkça ya da elle düşüldükçe burada görünür."
              />
            ) : (
              usages.map((u) => (
                <Box
                  key={u._id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    borderBottom: `1px solid ${COLORS.slate100}`,
                    opacity: u.reverted ? 0.55 : 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: TYPO.body, color: COLORS.slate800, textDecoration: u.reverted ? "line-through" : "none" }}>
                      {USAGE_SOURCE_LABELS[u.sourceType] || u.sourceType} · {dateTimeTr(u.at)}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
                      {u.reverted ? `Geri alındı · ${dateTimeTr(u.revertedAt)}` : u.note || "—"}
                    </Typography>
                  </Box>
                  {canManage && !u.reverted ? (
                    <Button
                      size="small"
                      startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setRevertTarget(u)}
                      sx={{
                        textTransform: "none",
                        fontSize: TYPO.label,
                        color: COLORS.slate600,
                        px: 1.25,
                        "&:hover": { bgcolor: alpha(palette.primary, 0.06) },
                      }}
                    >
                      Geri al
                    </Button>
                  ) : null}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </FormDrawer>

      <PromptDialog
        open={askUse}
        onClose={() => setAskUse(false)}
        onConfirm={async (note) => {
          await onConsume?.(pkg, note);
          setAskUse(false);
        }}
        title="Paketten seans düş"
        message={`"${pkg.template?.name}" paketinden bir seans düşülecek (kalan ${pkg.remaining}).`}
        inputLabel="Not (isteğe bağlı)"
        placeholder="Ör. önceki sistemden devreden seans"
        minRows={2}
        maxLength={300}
        confirmText="Seans düş"
        cancelText="Vazgeç"
        confirmVariant="primary"
        loading={busy}
      />
      <PromptDialog
        open={askCancel}
        onClose={() => setAskCancel(false)}
        onConfirm={async (reason) => {
          await onCancel?.(pkg, reason);
          setAskCancel(false);
        }}
        title="Paketi iptal et"
        message={`İptal edilen paket ${terms.patientGenLower} borcundan düşer; kullanılan seanslar geçmişte kalır.`}
        inputLabel="İptal gerekçesi"
        required
        minRows={2}
        maxLength={300}
        confirmText="İptal et"
        cancelText="Vazgeç"
        loading={busy}
      />
      <ConfirmDialog
        open={Boolean(revertTarget)}
        onClose={() => setRevertTarget(null)}
        onConfirm={async () => {
          await onRevert?.(pkg, revertTarget);
          setRevertTarget(null);
        }}
        title="Kullanımı geri al"
        message={
          revertTarget?.sourceType === "appointment"
            ? `Seans pakete iade edilir. Randevu tamamlanmışsa hizmet bedeli ${terms.patientGenLower} hesabına ayrıca yazılır.`
            : "Seans pakete iade edilir."
        }
        confirmText="Geri al"
        cancelText="Vazgeç"
        confirmVariant="warning"
        loading={busy}
      />
    </>
  );
}
